/**
 * La rivalutazione dei ruoli Logto: quando ha senso rileggerli, e perche' non basta
 * rileggerli una volta sola.
 *
 * 🔴 IL FATTO CHE RENDE NECESSARIO QUESTO FILE [M 2026-09-06]. `useHasLogtoRole`
 * memoizzava su `[authed, role]` e basta, e il suo commento lo dichiarava come una scelta:
 * «Does NOT recompute on token refresh mid-session — Acceptable trade-off: roles change
 * rarely and a stale read just defers the new privilege by one session boundary». Misurato
 * su `stg-tma` da or45: un utente a cui viene assegnato `beta` apre la sezione e vede una
 * schermata **vuota** pur avendo gia' nel token `["user","tester","beta"]`; al secondo
 * caricamento rende. Il differimento non era «di una sessione»: era di un caricamento, e
 * nel frattempo lo schermo non diceva niente.
 *
 * ⚠️ **E non c'e' niente a cui abbonarsi.** `persistTokens` (`lib/logto.ts:389-394`) scrive
 * `qlash.access_token` in `localStorage` e non emette nulla — nessun `CustomEvent`, nessun
 * dispatch Redux (verificato: zero occorrenze di `dispatchEvent`/`CustomEvent`/`subscribe`
 * in tutto `logto.ts`). E l'evento `storage` del browser **non si attiva nella scheda che
 * ha scritto**: e' pensato per le altre schede, e dentro la WebView di Telegram, che ha una
 * scheda sola, non arriverebbe in nessun caso.
 *
 * ⇒ Da qui **due** meccanismi, e non sono ridondanti:
 *  · il SEGNALE — `qlash:tokens-changed`, emesso da `persistTokens` subito dopo la scrittura
 *    (una riga, aggiunta con eccezione esplicita del seggio il 2026-09-06). Copre il caso che
 *    nessun evento del browser puo' vedere: **il rinnovo silenzioso mentre l'utente guarda
 *    fisso la schermata**;
 *  · la RETE — `visibilitychange`/`focus`. Copre il caso in cui il token e' cambiato senza
 *    passare da noi, o l'app e' stata ricaricata altrove: l'utente esce, gli assegnano il
 *    ruolo su Logto, rientra.
 * Il primo e' preciso e dipende da una riga in un altro file; la seconda non dipende da
 * nessuno. Tenere solo il segnale significherebbe che quella riga, cancellata da chiunque in
 * futuro, riporta il difetto in silenzio — e il gate non basta a impedirlo, perche' controlla
 * il testo, non il comportamento.
 *
 * Il modulo non importa nulla dell'app — niente `@/env`, niente `logto.ts`, niente React —
 * cosi' `tools/verify-limulo.ts` puo' esercitarlo sotto Node.
 */

export interface RoleRevalidation {
  /** Il contatore corrente. Cambia ⇒ chi memoizza su di esso rilegge. */
  version(): number;
  /** Segnala che i ruoli vanno riletti. */
  bump(): void;
  /** Si iscrive ai bump. Ritorna la funzione per disiscriversi. */
  subscribe(listener: () => void): () => void;
}

/**
 * ⚠️ **Un `Set` di listener, non un array**, e la ragione e' la disiscrizione: con un array
 * la stessa funzione iscritta due volte (due componenti che passano lo stesso callback
 * stabile) verrebbe rimossa una volta sola, e resterebbe un ascoltatore su un componente
 * smontato. Un `Set` rende l'iscrizione idempotente, che e' la semantica giusta qui: «questo
 * ascoltatore vuole saperlo», non «voglio saperlo n volte».
 */
export function createRoleRevalidation(): RoleRevalidation {
  let versione = 0;
  const ascoltatori = new Set<() => void>();
  return {
    version: () => versione,
    bump: () => {
      versione += 1;
      // ⚠️ Si itera su una COPIA: l'insieme avvisato e' quello che esisteva quando il bump
      // e' cominciato.
      //
      // 📌 Questa riga ha portato per un'ora una motivazione SBAGLIATA — «un ascoltatore che
      // si disiscrive dentro la propria notifica salterebbe il successivo». Misurato il
      // 2026-09-06: su un `Set`, cancellare l'elemento CORRENTE non salta niente, il
      // successivo viene eseguito lo stesso. La motivazione vera e' l'altra: se un
      // ascoltatore ne cancella un ALTRO, ancora da chiamare, sul Set vivo quello sparisce
      // — e sulla copia no. Cioe' l'essere avvisato o meno dipenderebbe dall'ordine di
      // iterazione del Set, che nessun chiamante controlla.
      //
      // La copia da' la semantica prevedibile, la stessa del `dispatchEvent` del DOM. E la
      // lezione che vale piu' della riga: una mutazione che NON arrossa il gate accusa il
      // test, e a volte — come qui — accusa il commento.
      for (const ascoltatore of [...ascoltatori]) ascoltatore();
    },
    subscribe: (listener) => {
      ascoltatori.add(listener);
      return () => ascoltatori.delete(listener);
    },
  };
}

/** L'istanza dell'applicazione: una sola, perche' il token e' uno solo. */
export const roleRevalidation = createRoleRevalidation();

/**
 * Chiede una rilettura dei ruoli. La chiama il bottone «Ricontrolla» del pannello di
 * `RoleGate`, e la chiamano gli ascoltatori di `visibilitychange`/`focus`.
 */
export function recheckLogtoRoles(): void {
  roleRevalidation.bump();
}

/**
 * L'evento che `persistTokens` emette dopo aver scritto i token
 * (`lib/logto.ts`). ⚠️ Il nome sta QUI ed e' importato di la': una stringa
 * scritta due volte e' un ascoltatore che smette di ascoltare senza che niente lo dica.
 */
export const TOKENS_CHANGED_EVENT = 'qlash:tokens-changed';

/**
 * Su cosa vale la pena rileggere, e **perche' proprio questi tre**.
 *
 * · `qlash:tokens-changed` — il segnale nostro. E' l'unico che copre il rinnovo silenzioso
 *   mentre l'utente guarda la schermata: nessun evento del browser lo vede.
 * · `visibilitychange` — il ritorno nella WebView di Telegram, che non perde e riacquista il
 *   focus della finestra come farebbe una scheda.
 * · `focus` — lo stesso ritorno, nel browser normale.
 * I due del browser non sono ridondanti rispetto al primo: coprono il token cambiato **senza
 * passare da noi**, e sopravvivono al giorno in cui qualcuno cancella quella riga.
 *
 * ⛔ Niente polling. Rileggere un JWT ogni N secondi per un dato che cambia una volta al mese
 * e' lavoro costante per un evento raro, e nasconderebbe la mancanza di un segnale invece di
 * lasciarla visibile.
 */
export function watchRoleRevalidationTriggers(
  target: {
    addEventListener(type: string, listener: () => void): void;
    removeEventListener(type: string, listener: () => void): void;
  },
  isVisible: () => boolean,
  onTrigger: () => void,
): () => void {
  const suVisibilita = () => {
    // ⚠️ Solo il ritorno alla visibilita', non l'uscita: `visibilitychange` scatta in
    // entrambi i versi, e rileggere mentre la app viene nascosta e' lavoro su un utente che
    // non c'e'.
    if (isVisible()) onTrigger();
  };
  target.addEventListener(TOKENS_CHANGED_EVENT, onTrigger);
  target.addEventListener('visibilitychange', suVisibilita);
  target.addEventListener('focus', onTrigger);
  return () => {
    target.removeEventListener(TOKENS_CHANGED_EVENT, onTrigger);
    target.removeEventListener('visibilitychange', suVisibilita);
    target.removeEventListener('focus', onTrigger);
  };
}

/** L'esito del gate, dichiarato invece che dedotto da un booleano. */
export type RoleGateOutcome = 'unauthenticated' | 'granted' | 'missing';

/**
 * Chi puo' passare. **`unauthenticated` e `missing` sono due cose diverse** e il tipo lo dice:
 * la prima e' «non sappiamo chi sei» (e ci pensa il gate d'autenticazione, molto piu' su
 * nell'albero), la seconda e' «sappiamo chi sei e questo ruolo non ce l'hai» — l'unica delle
 * due su cui abbia senso mostrare un pannello con «Ricontrolla».
 *
 * Collassarle su un booleano e' esattamente cio' che faceva perdere l'informazione: `false`
 * diventava `fallback`, e `fallback` era `null`.
 */
/**
 * «Ha almeno uno di questi ruoli?» — la domanda di `AdminGate`, che a differenza di
 * `RoleGate` non gatta su UN ruolo ma su un insieme (`administrator` **o** `beta`).
 *
 * ⚠️ **Un elenco vuoto di ruoli richiesti risponde `false`, non `true`.** La lettura
 * matematica («nessun requisito ⇒ tutti passano») e' la piu' pericolosa possibile su un
 * gate: una costante svuotata per sbaglio aprirebbe la build DEV a chiunque, con la build
 * verde e nessun segnale. Qui «non e' stato richiesto nessun ruolo» significa «nessuno
 * passa», che e' il verso in cui un errore si nota.
 */
export function hasAnyRole(roles: readonly string[], required: readonly string[]): boolean {
  if (required.length === 0) return false;
  return required.some((r) => roles.includes(r));
}

export function roleGateOutcome(
  authenticated: boolean,
  roles: readonly string[],
  required: string,
): RoleGateOutcome {
  if (!authenticated) return 'unauthenticated';
  return roles.includes(required) ? 'granted' : 'missing';
}
